import { Component, OnDestroy, OnInit } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { Subscription } from 'rxjs';
import { ControlmonitorService, MonitoringDailyRow, MonitoringTenantRow } from '../../services/controlmonitor.service';
import { UserSessionService } from '../../services/user-session.service';

@Component({
  selector: 'app-controlmonitor',
  templateUrl: './controlmonitor.component.html',
  styleUrls: ['./controlmonitor.component.scss']
})
export class ControlmonitorComponent implements OnInit, OnDestroy {
  tenantId: number | null = null;
  loading = false;
  error = '';

  usersByDay: MonitoringDailyRow[] = [];
  accessesByDay: MonitoringDailyRow[] = [];
  topTenants: MonitoringTenantRow[] = [];

  usersChartData: ChartData<'line'> = { labels: [], datasets: [] };
  accessesChartData: ChartData<'line'> = { labels: [], datasets: [] };
  donutChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };

  lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    animation: { duration: 420 },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 10,
          boxHeight: 10,
          color: '#5d7594',
          font: { size: 14, weight: 'bold' }
        }
      },
      tooltip: {
        backgroundColor: '#15385f',
        titleColor: '#ffffff',
        bodyColor: '#e9f1ff',
        displayColors: true,
        cornerRadius: 10,
        padding: 10
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#7f92aa',
          font: { size: 12, weight: 'bold' },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#e7eef8' },
        ticks: {
          color: '#7f92aa',
          font: { size: 11, weight: 600 },
          precision: 0
        }
      }
    }
  };

  donutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#15385f',
        titleColor: '#ffffff',
        bodyColor: '#e9f1ff',
        cornerRadius: 10,
        padding: 10
      }
    }
  };

  private userSub: Subscription | null = null;

  constructor(
    private monitoringService: ControlmonitorService,
    private userSession: UserSessionService
  ) {}

  ngOnInit(): void {
    this.userSub = this.userSession.user$.subscribe((user) => {
      this.tenantId = Number(user?.tenant_id || user?.tenant?.id || 0) || null;
      if (this.tenantId === 1) {
        this.loadDashboard();
      }
    });

    const current = this.userSession.getUser();
    this.tenantId = Number(current?.tenant_id || current?.tenant?.id || 0) || null;
    if (this.tenantId === 1) {
      this.loadDashboard();
    }
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  loadDashboard(): void {
    if (this.tenantId !== 1) {
      return;
    }
    this.loading = true;
    this.error = '';

    this.monitoringService.getSystemMonitoring(this.tenantId).subscribe({
      next: (res) => {
        this.usersByDay = Array.isArray(res?.users_by_day) ? res.users_by_day : [];
        this.accessesByDay = Array.isArray(res?.accesses_by_day) ? res.accesses_by_day : [];
        this.topTenants = Array.isArray(res?.top_tenants) ? res.top_tenants : [];

        this.rebuildCharts();
        this.loading = false;
      },
      error: () => {
        this.error = 'Não foi possível carregar os dados de monitoramento.';
        this.loading = false;
      }
    });
  }

  get usersAverage(): number {
    return this.average(this.usersByDay.map((item) => Number(item?.value || 0)));
  }

  get accessesAverage(): number {
    return this.average(this.accessesByDay.map((item) => Number(item?.value || 0)));
  }

  get topTenantTotal(): number {
    return this.topTenants.reduce((acc, item) => acc + Number(item?.accesses || 0), 0);
  }

  tenantPercent(item: MonitoringTenantRow): number {
    const total = this.topTenantTotal;
    if (!total) {
      return 0;
    }
    return Number(((Number(item?.accesses || 0) / total) * 100).toFixed(1));
  }

  tenantColor(index: number): string {
    const palette = ['#22b8b3', '#4c6ef5', '#f0bf45', '#ef6464', '#4fc3f7', '#7c8cff', '#53d3a1', '#8b9db5'];
    return palette[index % palette.length];
  }

  private rebuildCharts(): void {
    const usersLabels = this.usersByDay.map((item) => this.formatDayMonth(item?.date || ''));
    const usersValues = this.usersByDay.map((item) => Number(item?.value || 0));
    const usersAvgSeries = usersValues.map(() => Number(this.usersAverage.toFixed(1)));

    this.usersChartData = {
      labels: usersLabels,
      datasets: [
        {
          label: 'Usuários',
          data: usersValues,
          backgroundColor: 'rgba(34, 184, 179, 0.22)',
          borderColor: '#22b8b3',
          borderWidth: 3,
          pointBackgroundColor: '#22b8b3',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Média',
          data: usersAvgSeries,
          borderColor: '#8ea4c7',
          borderWidth: 2,
          borderDash: [6, 6],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0
        }
      ]
    };

    const accessLabels = this.accessesByDay.map((item) => this.formatDayMonth(item?.date || ''));
    const accessValues = this.accessesByDay.map((item) => Number(item?.value || 0));
    const accessAvgSeries = accessValues.map(() => Number(this.accessesAverage.toFixed(1)));

    this.accessesChartData = {
      labels: accessLabels,
      datasets: [
        {
          label: 'Acessos',
          data: accessValues,
          backgroundColor: 'rgba(76, 110, 245, 0.2)',
          borderColor: '#4c6ef5',
          borderWidth: 3,
          pointBackgroundColor: '#4c6ef5',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Média',
          data: accessAvgSeries,
          borderColor: '#8ea4c7',
          borderWidth: 2,
          borderDash: [6, 6],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0
        }
      ]
    };

    this.donutChartData = {
      labels: this.topTenants.map((item) => item?.tenant_name || '-'),
      datasets: [
        {
          data: this.topTenants.map((item) => Number(item?.accesses || 0)),
          backgroundColor: this.topTenants.map((_item, index) => this.tenantColor(index)),
          borderWidth: 0,
          hoverOffset: 8
        }
      ]
    };
  }

  private average(values: number[]): number {
    if (!values.length) {
      return 0;
    }
    const total = values.reduce((acc, value) => acc + Number(value || 0), 0);
    return total / values.length;
  }

  private formatDayMonth(dateInput: string): string {
    if (!dateInput) {
      return '';
    }
    const date = new Date(`${String(dateInput).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }
}
